import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, FileText, Check, X, Send, Eye, DollarSign } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  draft: 'bg-slate-100 text-slate-600',
  pending_approval: 'bg-amber-100 text-amber-700',
  approved: 'bg-blue-100 text-blue-700',
  sent: 'bg-purple-100 text-purple-700',
  paid: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-600',
};

export default function InvoicesPage() {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const [invoices, setInvoices] = useState([]);
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [form, setForm] = useState({ case_id: '', client_id: '', items: [{ description: '', amount: 0 }], due_date: '', notes: '' });

  const fetchData = useCallback(async () => {
    try {
      const [invRes, casesRes, pmRes] = await Promise.all([
        axios.get(`${API}/invoices`, { withCredentials: true }),
        axios.get(`${API}/cases`, { withCredentials: true }),
        axios.get(`${API}/payment-methods`, { withCredentials: true }),
      ]);
      setInvoices(invRes.data);
      setCases(casesRes.data);
      setPaymentMethods(pmRes.data);
      if (user?.role === 'admin' || user?.role === 'lawyer') {
        const usersRes = await axios.get(`${API}/users?role=client`, { withCredentials: true });
        setClients(usersRes.data);
      }
    } catch {}
  }, [user?.role]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addItem = () => setForm({ ...form, items: [...form.items, { description: '', amount: 0 }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, field, val) => {
    const items = [...form.items];
    items[i] = { ...items[i], [field]: field === 'amount' ? parseFloat(val) || 0 : val };
    setForm({ ...form, items });
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/invoices`, form, { withCredentials: true });
      setShowCreate(false);
      setForm({ case_id: '', client_id: '', items: [{ description: '', amount: 0 }], due_date: '', notes: '' });
      fetchData();
    } catch {}
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}`, { status: newStatus }, { withCredentials: true });
      fetchData();
      setViewInvoice(null);
    } catch {}
  };

  const total = form.items.reduce((s, i) => s + (i.amount || 0), 0);
  const isAdminOrLawyer = user?.role === 'admin' || user?.role === 'lawyer';

  return (
    <DashboardLayout>
      <div data-testid="invoices-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] font-heading">
            {lang === 'ar' ? 'الفواتير' : 'Invoices'}
          </h1>
          {isAdminOrLawyer && (
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
              <DialogTrigger asChild>
                <Button className="bg-[#B48E4B] text-white hover:bg-[#8C6A32]" data-testid="create-invoice-btn">
                  <Plus className="w-4 h-4 me-2" /> {lang === 'ar' ? 'فاتورة جديدة' : 'New Invoice'}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading">{lang === 'ar' ? 'إنشاء فاتورة' : 'Create Invoice'}</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>{lang === 'ar' ? 'القضية' : 'Case'}</Label>
                    <Select value={form.case_id} onValueChange={v => setForm({ ...form, case_id: v })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{cases.map(c => <SelectItem key={c.case_id} value={c.case_id}>{c.title}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{lang === 'ar' ? 'العميل' : 'Client'}</Label>
                    <Select value={form.client_id} onValueChange={v => setForm({ ...form, client_id: v })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{clients.map(c => <SelectItem key={c.user_id} value={c.user_id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{lang === 'ar' ? 'البنود' : 'Items'}</Label>
                    {form.items.map((item, i) => (
                      <div key={i} className="flex gap-2 mt-2">
                        <Input placeholder={lang === 'ar' ? 'الوصف' : 'Description'} value={item.description} onChange={e => updateItem(i, 'description', e.target.value)} className="flex-1" />
                        <Input type="number" placeholder="0" value={item.amount || ''} onChange={e => updateItem(i, 'amount', e.target.value)} className="w-24" />
                        {form.items.length > 1 && <button onClick={() => removeItem(i)} className="text-red-500 p-1"><X className="w-4 h-4" /></button>}
                      </div>
                    ))}
                    <button onClick={addItem} className="text-sm text-[#B48E4B] mt-2">{lang === 'ar' ? '+ إضافة بند' : '+ Add Item'}</button>
                  </div>
                  <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="font-medium">{lang === 'ar' ? 'الإجمالي' : 'Total'}</span>
                    <span className="font-bold text-[#0F172A]">{total.toFixed(2)} JOD</span>
                  </div>
                  <div>
                    <Label>{lang === 'ar' ? 'تاريخ الاستحقاق' : 'Due Date'}</Label>
                    <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label>{lang === 'ar' ? 'ملاحظات' : 'Notes'}</Label>
                    <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm min-h-[60px]" />
                  </div>
                  <Button onClick={handleCreate} className="w-full bg-[#0F172A] text-white hover:bg-[#1E293B]" data-testid="submit-invoice">
                    {lang === 'ar' ? 'إنشاء الفاتورة' : 'Create Invoice'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Invoice detail dialog */}
        <Dialog open={!!viewInvoice} onOpenChange={o => { if (!o) setViewInvoice(null); }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">{lang === 'ar' ? 'تفاصيل الفاتورة' : 'Invoice Details'}</DialogTitle></DialogHeader>
            {viewInvoice && (
              <div className="space-y-4 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-[#94A3B8] font-mono">#{viewInvoice.invoice_id}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[viewInvoice.status]}`}>{viewInvoice.status?.replace('_', ' ')}</span>
                </div>
                <div className="space-y-2">
                  {viewInvoice.items?.map((item, i) => (
                    <div key={i} className="flex justify-between p-2 bg-slate-50 rounded">
                      <span className="text-sm">{item.description}</span>
                      <span className="text-sm font-medium">{item.amount} JOD</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between p-3 bg-[#0F172A] text-white rounded-lg">
                  <span>{lang === 'ar' ? 'الإجمالي' : 'Total'}</span>
                  <span className="font-bold">{viewInvoice.total} JOD</span>
                </div>
                {viewInvoice.due_date && <p className="text-sm text-[#475569]">{lang === 'ar' ? 'الاستحقاق:' : 'Due:'} {viewInvoice.due_date}</p>}
                {viewInvoice.notes && <p className="text-sm text-[#475569]">{viewInvoice.notes}</p>}
                {paymentMethods.length > 0 && viewInvoice.status === 'sent' && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs font-medium text-[#94A3B8] uppercase mb-2">{lang === 'ar' ? 'طرق الدفع' : 'Payment Methods'}</p>
                    {paymentMethods.filter(pm => pm.is_active).map(pm => (
                      <div key={pm.pm_id} className="text-sm text-[#475569] mb-1">
                        <span className="font-medium">{lang === 'ar' ? pm.name_ar : pm.name}:</span> {lang === 'ar' ? pm.details_ar : pm.details}
                      </div>
                    ))}
                  </div>
                )}
                {/* Admin actions */}
                {user?.role === 'admin' && (
                  <div className="flex gap-2 flex-wrap">
                    {viewInvoice.status === 'draft' && (
                      <Button onClick={() => handleStatusChange(viewInvoice.invoice_id, 'approved')} className="bg-green-600 text-white hover:bg-green-700" data-testid="approve-invoice">
                        <Check className="w-4 h-4 me-1" /> {lang === 'ar' ? 'موافقة' : 'Approve'}
                      </Button>
                    )}
                    {viewInvoice.status === 'approved' && (
                      <Button onClick={() => handleStatusChange(viewInvoice.invoice_id, 'sent')} className="bg-[#B48E4B] text-white hover:bg-[#8C6A32]" data-testid="send-invoice">
                        <Send className="w-4 h-4 me-1" /> {lang === 'ar' ? 'إرسال للعميل' : 'Send to Client'}
                      </Button>
                    )}
                    {viewInvoice.status === 'sent' && (
                      <Button onClick={() => handleStatusChange(viewInvoice.invoice_id, 'paid')} className="bg-green-600 text-white hover:bg-green-700" data-testid="mark-paid">
                        <DollarSign className="w-4 h-4 me-1" /> {lang === 'ar' ? 'تم الدفع' : 'Mark Paid'}
                      </Button>
                    )}
                    {!['paid', 'cancelled'].includes(viewInvoice.status) && (
                      <Button variant="outline" onClick={() => handleStatusChange(viewInvoice.invoice_id, 'cancelled')} className="text-red-600 border-red-200">
                        <X className="w-4 h-4 me-1" /> {lang === 'ar' ? 'إلغاء' : 'Cancel'}
                      </Button>
                    )}
                  </div>
                )}
                {user?.role === 'lawyer' && viewInvoice.status === 'draft' && (
                  <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">{lang === 'ar' ? 'بانتظار موافقة المدير' : 'Pending admin approval'}</p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Card className="border-slate-200 shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">ID</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{lang === 'ar' ? 'الإجمالي' : 'Total'}</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{lang === 'ar' ? 'الحالة' : 'Status'}</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{lang === 'ar' ? 'التاريخ' : 'Date'}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map(inv => (
                <TableRow key={inv.invoice_id} className="border-b border-slate-100">
                  <TableCell className="text-xs font-mono text-[#94A3B8]">{inv.invoice_id?.slice(0, 12)}</TableCell>
                  <TableCell className="font-medium text-[#0F172A]">{inv.total} JOD</TableCell>
                  <TableCell><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[inv.status]}`}>{inv.status?.replace('_', ' ')}</span></TableCell>
                  <TableCell className="text-sm text-[#94A3B8]">{new Date(inv.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <button onClick={() => setViewInvoice(inv)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`view-invoice-${inv.invoice_id}`}>
                      <Eye className="w-4 h-4 text-[#475569]" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
              {invoices.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-[#94A3B8]">
                  <FileText className="w-10 h-10 mx-auto mb-2 text-[#94A3B8]" />
                  {lang === 'ar' ? 'لا توجد فواتير' : 'No invoices yet'}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </DashboardLayout>
  );
}
